import React, { useEffect, useState } from 'react';
import { Col, ConfigProvider, Flex, Layout, Row, theme, Typography } from 'antd';
import LayoutSider from '../../components/brief/LayoutSider';
import dayjs, { Dayjs } from 'dayjs';
import Filter from './Filter';
import '../../assets/reports.scss';
import AllProducts, { AllProductsReport } from './AllProducts';
import IndicatorsComparison from './IndicatorsComparison';
import { t } from 'i18next';
import Roles from '../../roles/Roles';
import Forbidden from '../results/forbidden';

const { Title } = Typography;

const dateFormat = 'DD.MM.YYYY';

const { Header, Content } = Layout;

export type UserValue = {
  label?: string,
  value?: string,
}

export interface ReportsFilter {
  pools?: UserValue[],
  companies?: UserValue[],
  products?: UserValue[],
  kpis?: UserValue[],
  period?: [Dayjs | null, Dayjs | null] | null,
  channels?: string[],
}

const filterId = 'Reports_Filters';

const getFilterFromLocalStorage = (): ReportsFilter => {
  const filter = localStorage.getItem(filterId) || '';

  try {
    const reportsFilter = JSON.parse(filter);

    if (reportsFilter) {
      if (reportsFilter.period && reportsFilter.period[0] && reportsFilter.period[1]) {
        reportsFilter.period[0] = dayjs(reportsFilter.period[0], 'DD.MM.YYYY');
        reportsFilter.period[1] = dayjs(reportsFilter.period[1], 'DD.MM.YYYY');
      }

      return reportsFilter;
    }
  } catch (error) {
  }

  return {};
};

export default function Reports () {
  const [mainFilter, setMainFilter] = useState<ReportsFilter>(getFilterFromLocalStorage());
  const [activeFilter, setActiveFilter] = useState<ReportsFilter>(mainFilter);
  const [selectedItem, setSelectedItem] = useState<AllProductsReport | undefined>();

  useEffect(() => {
    if (selectedItem) {
      setMainFilter(activeFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  useEffect(() => {
    if (!selectedItem) {
      setActiveFilter(mainFilter);
    }
  }, [selectedItem, mainFilter]);

  useEffect(() => {
    const filterToSave = structuredClone(mainFilter);

    if (mainFilter.period?.[0] && mainFilter.period?.[1]) {
      filterToSave.period = [
        mainFilter.period[0].format(dateFormat),
        mainFilter.period[1].format(dateFormat),
      ] as any;
    }

    localStorage.setItem(filterId, JSON.stringify(filterToSave));
  }, [mainFilter]);

  const handleFilterChange = (newFilter: ReportsFilter) => {
    setActiveFilter(newFilter);
    if (!selectedItem) {
      setMainFilter(newFilter);
    }
  };

  const {
    token: { borderRadiusLG },
  } = theme.useToken();
  return (
    <ConfigProvider
      theme={{
        components: {
          Layout: {
            headerBg: '#fff',
            siderBg: '#fff',
            fontFamily: 'Open Sans',
            fontFamilyCode: 'Open Sans',
          },
        },
      }}
    >
      <Layout>
        <Header
          className={'layout-header'}>
          <Row justify={'space-between'} style={{ flex: 1 }} align={'middle'}>
            <Col span={8}></Col>
            <Col span={8}>
              <Flex justify={'center'}><Title level={5} style={{ margin: 0 }}>{t('reports.allProducts')}</Title></Flex>
            </Col>
            <Col span={8}></Col>
          </Row>
        </Header>
        <Layout style={{ paddingLeft: '48px' }}>
          <LayoutSider/>
          <Layout>
            <Content
              style={{
                minHeight: 'calc(100vh - 96px)',
                background: '#F1F5F7',
                borderRadius: borderRadiusLG,
                padding: '16px',
              }}
            >
              {Roles.hasAccess('canReadReports')
                ?
                <Flex vertical gap={16}>
                  <Flex vertical gap={5}>
                    <Filter
                      reportsFilter={activeFilter}
                      setReportsFilter={handleFilterChange}
                      selectedItem={selectedItem}
                    />
                  </Flex>
                  <AllProducts
                    reportsFilter={activeFilter}
                    selectedItem={selectedItem}
                    setSelectedItem={setSelectedItem}
                  />
                  {selectedItem &&
                    <IndicatorsComparison
                      reportsFilter={activeFilter}
                      selectedItem={selectedItem}
                      setReportsFilter={handleFilterChange}
                    />
                  }
                </Flex>
                : <Forbidden/>
              }
            </Content>
          </Layout>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}
